import { useState, type CSSProperties, type ReactElement } from 'react';
import { v2, parseApiErrorBody } from '@/api/v2Client';

/**
 * Phase UI-13 — Inspector reclassification override.
 *
 * Lets an authorized user override a document's CtdSectionCode with a
 * mandatory rationale. PATCHes via the existing /placement endpoint
 * (which already requires `reason` when the new section differs from
 * the classifier suggestion).
 */

export interface InspectorOverrideSectionProps {
  readonly documentId: string;
  readonly currentSectionCode: string | null;
  readonly sections: ReadonlyArray<{ code: string; label: string }>;
  readonly canEdit: boolean;
  readonly onSuccess?: (newSectionCode: string) => void;
}

const wrapperStyle: CSSProperties = {
  border: '1px dashed var(--border)',
  borderRadius: 4,
  padding: 8,
  marginTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
};

export function InspectorOverrideSection({
  documentId,
  currentSectionCode,
  sections,
  canEdit,
  onSuccess,
}: InspectorOverrideSectionProps): ReactElement | null {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<string>(currentSectionCode ?? '');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canEdit) return null;

  const submit = async (): Promise<void> => {
    if (!section.trim() || !rationale.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await v2.documents.updatePlacement(documentId, {
        newSectionCode: section.trim(),
        reason: rationale.trim(),
      });
      setOpen(false);
      setRationale('');
      if (onSuccess) onSuccess(section.trim());
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="override-section-btn"
        onClick={() => setOpen(true)}
        style={{
          marginTop: 8,
          padding: '4px 10px',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          fontSize: 12,
          cursor: 'pointer',
          alignSelf: 'flex-start',
        }}
      >
        Override section
      </button>
    );
  }

  return (
    <div data-testid="override-section-form" style={wrapperStyle}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontWeight: 600 }}>Section</span>
        <select
          data-testid="override-section-select"
          value={section}
          onChange={(e) => setSection(e.target.value)}
          style={{ padding: '4px 6px', fontSize: 12 }}
        >
          <option value="">(select section)</option>
          {sections.map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} — {s.label}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontWeight: 600 }}>Rationale (required)</span>
        <textarea
          data-testid="override-rationale"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={3}
          placeholder="Why are you overriding the AI classification?"
          style={{
            padding: 6,
            fontSize: 12,
            border: '1px solid var(--border)',
            borderRadius: 3,
            resize: 'vertical',
          }}
        />
      </label>
      {error && (
        <div role="alert" data-testid="override-error" style={{ color: 'var(--danger, #b91c1c)' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <button
          type="button"
          data-testid="override-cancel"
          disabled={busy}
          onClick={() => setOpen(false)}
          style={{
            padding: '3px 10px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 3,
            fontSize: 12,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="override-submit"
          disabled={busy || !section.trim() || !rationale.trim()}
          onClick={() => void submit()}
          style={{
            padding: '3px 10px',
            background: 'var(--accent, #4f46e5)',
            color: '#fff',
            border: 'none',
            borderRadius: 3,
            fontSize: 12,
            fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy || !section.trim() || !rationale.trim() ? 0.5 : 1,
          }}
        >
          {busy ? 'Saving…' : 'Submit override'}
        </button>
      </div>
    </div>
  );
}
